import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Check, Copy, Loader2, Tag } from 'lucide-react'
import { getCoupons } from '../../lib/api/booking'
import { useAuth } from '../../store/authStore'
import { prettyDayShort } from '../../lib/format'
import { cn } from '../../lib/cn'
import { PageShell } from '../../components/layout/PageShell'
import type { Locale } from '../../lib/api/types'

/**
 * The codes this account can use. Copy one, then paste it at checkout — nothing is applied for the
 * customer, so nothing is applied behind their back either.
 *
 * Keyed by account: an offer usable once per account disappears once it has been spent, and the
 * server decides that, not this page.
 */
export function CouponsPage() {
  const { t, i18n } = useTranslation()
  const locale = (i18n.language as Locale) ?? 'it'
  const user = useAuth((s) => s.user)
  const [copied, setCopied] = useState<string | null>(null)

  // Keyed by account so signing in or out refetches rather than showing the previous answer.
  const { data, isLoading } = useQuery({
    queryKey: ['coupons', user?.email ?? null],
    queryFn: getCoupons,
    staleTime: 60_000,
    retry: false,
  })

  async function copy(code: string) {
    try {
      await navigator.clipboard.writeText(code)
    } catch {
      /* clipboard blocked — the code stays on screen to type by hand */
    }
    setCopied(code)
    window.setTimeout(() => setCopied((c) => (c === code ? null : c)), 1800)
  }

  const coupons = data ?? []

  return (
    <PageShell>
      <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink-900">{t('coupons.title')}</h1>
      <p className="mt-1 text-sm text-ink-500">{t('coupons.subtitle')}</p>

      {isLoading ? (
        <div className="mt-8 flex justify-center text-ink-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : coupons.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-ink-100 bg-ink-50/60 px-4 py-8 text-center">
          <Tag className="mx-auto h-6 w-6 text-ink-300" />
          <p className="mt-2 text-sm text-ink-500">{user ? t('coupons.empty') : t('coupons.loginHint')}</p>
        </div>
      ) : (
        <ul className="mt-5 space-y-3">
          {coupons.map((c) => (
            <li key={c.code} className="rounded-2xl border border-ink-100 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,.04)]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display text-[22px] font-extrabold uppercase tracking-wide text-brand-600">{c.code}</p>
                  {c.validTo && (
                    <p className="mt-0.5 text-[12.5px] text-ink-500">
                      {t('coupons.validUntil', { date: prettyDayShort(c.validTo, locale) })}
                    </p>
                  )}
                  {c.name && <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-700">{c.name}</p>}
                  {c.oncePerAccount && (
                    <p className="mt-1 text-[12px] font-semibold text-ink-400">{t('coupons.oncePerAccount')}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => copy(c.code)}
                  className={cn(
                    'inline-flex flex-none items-center gap-1.5 rounded-xl px-3 py-2 text-[13px] font-semibold transition-colors',
                    copied === c.code ? 'bg-ink-900 text-white' : 'bg-brand-50 text-brand-700 hover:bg-brand-100',
                  )}
                >
                  {copied === c.code ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied === c.code ? t('coupons.copied') : t('coupons.copy')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-6 text-[12.5px] leading-relaxed text-ink-500">{t('coupons.howTo')}</p>
    </PageShell>
  )
}
